import { getBlob } from "../Services/ApiBase";
import { API_ENDPOINTS } from "../Services/ApiEndPoints";
import { getApiErrorMessage, isSilentRequestAbortError } from "./apiErrorMessage";

function parseFilenameFromContentDisposition(header) {
  if (!header || typeof header !== "string") return null;
  const utf8 = /filename\*=UTF-8''([^;\s]+)/i.exec(header);
  if (utf8) {
    try {
      return decodeURIComponent(utf8[1]);
    } catch {
      return null;
    }
  }
  const quoted = /filename="([^"]+)"/i.exec(header);
  if (quoted) return quoted[1].trim();
  const plain = /filename=([^;\s]+)/i.exec(header);
  return plain ? plain[1].trim().replace(/^["']|["']$/g, "") : null;
}

async function blobToErrorMessage(blob) {
  try {
    const text = await blob.text();
    const j = JSON.parse(text);
    return j.message || j.error || j.data?.message || text || "Export failed";
  } catch {
    return "Export failed";
  }
}

function omitEmptyParams(params) {
  const out = {};
  if (!params || typeof params !== "object") return out;
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null) continue;
    if (typeof v === "string" && v.trim() === "") continue;
    out[k] = v;
  }
  return out;
}

async function downloadTimeLogsBlob(url, params, fallbackFilename) {
  const query = omitEmptyParams(params);

  try {
    const response = await getBlob(url, query);
    const blob = response.data;
    const contentType = String(
      response.headers?.["content-type"] || response.headers?.["Content-Type"] || ""
    );

    if (contentType.includes("application/json")) {
      throw new Error(await blobToErrorMessage(blob));
    }

    const cd =
      response.headers?.["content-disposition"] || response.headers?.["Content-Disposition"];
    const filename = parseFilenameFromContentDisposition(cd) || fallbackFilename;

    const objectUrl = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = objectUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(objectUrl);
  } catch (err) {
    if (isSilentRequestAbortError(err)) throw err;
    const data = err?.response?.data;
    if (data instanceof Blob) {
      const msg = await blobToErrorMessage(data);
      throw new Error(msg, { cause: err });
    }
    throw new Error(getApiErrorMessage(err, "Export failed"), { cause: err });
  }
}

/** GET /time-logs/my/export — current user's timesheet CSV/XLSX. */
export function downloadMyTimesheetExport(params, format = "csv") {
  const fmt = format === "xlsx" ? "xlsx" : "csv";
  return downloadTimeLogsBlob(
    API_ENDPOINTS.TIME_ENTRIES.EXPORT_MY,
    { ...params, format: fmt },
    `my-timesheet-${params?.dateFrom ?? "range"}.${fmt}`
  );
}

/** GET /time-logs/team-summary/export — CSV/XLSX. */
export function downloadTeamSummaryExport(params, format = "csv") {
  const fmt = format === "xlsx" ? "xlsx" : "csv";
  return downloadTimeLogsBlob(
    API_ENDPOINTS.TIME_ENTRIES.EXPORT_TEAM_SUMMARY,
    { ...params, format: fmt },
    `team-time-summary-${params?.dateFrom ?? "range"}.${fmt}`
  );
}

/** GET /time-logs/project-summary/export — CSV/XLSX. */
export function downloadProjectSummaryExport(params, format = "csv") {
  const fmt = format === "xlsx" ? "xlsx" : "csv";
  return downloadTimeLogsBlob(
    API_ENDPOINTS.TIME_ENTRIES.EXPORT_PROJECT_SUMMARY,
    { ...params, format: fmt },
    `project-time-summary-${params?.dateFrom ?? "range"}.${fmt}`
  );
}

/** GET /time-logs/developer-summary/export — CSV/XLSX. */
export function downloadDeveloperSummaryExport(params, format = "csv") {
  const fmt = format === "xlsx" ? "xlsx" : "csv";
  return downloadTimeLogsBlob(
    API_ENDPOINTS.TIME_ENTRIES.EXPORT_DEVELOPER_SUMMARY,
    { ...params, format: fmt },
    `developer-time-summary-${params?.dateFrom ?? "range"}.${fmt}`
  );
}

/** GET /time-logs/export — All Time Entries CSV/XLSX. */
export function downloadAllTimeLogsExport(params, format = "csv") {
  const fmt = format === "xlsx" ? "xlsx" : "csv";
  return downloadTimeLogsBlob(
    API_ENDPOINTS.TIME_ENTRIES.EXPORT_ALL_TIME_LOGS,
    { ...params, format: fmt },
    `all-time-entries-${params?.dateFrom ?? "range"}.${fmt}`
  );
}
